import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"
import _imports_0 from '@/assets.app/images/logo.svg'
import _imports_1 from '@/assets.app/images/onboarding/email.svg'
import _imports_2 from '@/assets.app/images/onboarding/separator-line.svg'


const _hoisted_1 = { class: "onboarding-signup" }
const _hoisted_2 = {
  key: 0,
  class: "onboarding-signup__loading-screen"
}
const _hoisted_3 = { class: "onboarding-signup__loading-screen-inner" }
const _hoisted_4 = { class: "onboarding-signup__banner" }
const _hoisted_5 = { class: "onboarding-signup__form--tablet" }
const _hoisted_6 = { class: "onboarding-signup__links" }
const _hoisted_7 = { class: "onboarding-signup__primary-button-wrapper" }
const _hoisted_8 = { class: "onboarding-signup__login" }
const _hoisted_9 = {
  key: 1,
  class: "sign-up onboarding-signup__form--desktop"
}
const _hoisted_10 = { class: "sign-up__form-box" }
const _hoisted_11 = { class: "shortform-sign-up" }
const _hoisted_12 = { class: "shortform-sign-up__group" }
const _hoisted_13 = { class: "shortform-sign-up__group" }
const _hoisted_14 = { class: "shortform-sign-up__group" }
const _hoisted_15 = {
  key: 0,
  class: "text-muted font-italic"
}
const _hoisted_16 = {
  key: 0,
  class: "text-success font-weight-700"
}
const _hoisted_17 = {
  key: 1,
  class: "text-info font-weight-700"
}
const _hoisted_18 = {
  key: 2,
  class: "text-warning font-weight-700"
}
const _hoisted_19 = {
  key: 3,
  class: "text-danger font-weight-700"
}
const _hoisted_20 = {
  key: 4,
  class: "text-danger font-weight-700"
}
const _hoisted_21 = {
  key: 1,
  class: "alert alert-danger"
}
const _hoisted_22 = { class: "shortform-sign-up__group" }
const _hoisted_23 = { class: "shortform-sign-up__terms" }
const _hoisted_24 = { class: "shortform-sign-up__group" }
const _hoisted_25 = { class: "shortform-sign-up__submit button button--theme" }
const _hoisted_26 = { class: "shortform-sign-up__log-in" }

import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import { SignupView } from '@/models/auth/models'
import { AuthMode, GoogleAuthView } from '@/models/auth/google'
import { auth } from '@/services/auth'
import {
  createSocialAuthView,
  SocialAuthComponentView,
} from '@/models/auth/social'
import { authCssFix } from '@/helpers/html'
import { assertNotNull } from '@/helpers/typing'
import { TABLET_WIDTH } from '@/init/settings'
import { platform } from '@/services/platform'
import { isAppleDevice } from '@/helpers/userAgent'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import SocialAuth from '@/app/components/auth/SocialAuth.vue'
import AppleSignInButton from '@/app/components/auth/AppleSignInButton.vue'
import FacebookLoginButton from '@/app/components/auth/FacebookLoginButton.vue'
import GoogleSignInButton from '@/app/components/auth/GoogleSignInButton.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import BottomSheet from '@/app/components/BottomSheet.vue'
import SignupSheet from '../signup/SignupSheet.vue'
import Slider from '../signup/Slider.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'Signup',
  props: {
    nextOnboardingPage: { type: Function }
  },
  setup(__props: any) {

const props = __props

const route = useRoute()
const router = useRouter()
const wait = useWait()

const view = ref(new SignupView(onStart, onSuccess, onError))
const socialView = ref<SocialAuthComponentView | null>(null)

const authMode = ref<AuthMode>(AuthMode.Signup)

const bottomSheetRef = ref<InstanceType<typeof BottomSheet> | null>(null)

const showGoogleSignupOnMobile = ref(false)
const primaryButton = ref<'apple' | 'google'>('google')
const isGoogleNative = ref(true)

function updateShowGoogleSignupOnMobile(): void {
  showGoogleSignupOnMobile.value = window.innerWidth <= TABLET_WIDTH

  if (
    socialView.value &&
    socialView.value.googleAuthView instanceof GoogleAuthView
  ) {
    const googleAuthView = socialView.value.googleAuthView as GoogleAuthView
    setTimeout(() => googleAuthView.renderButtons(), 100)
  }
}

function onStart(): void {
  wait.start('Register')
}
function openInfoSheet(): void {
  bottomSheetRef.value?.view.open()
}

async function onSuccess(nextPage: any): Promise<void> {
  wait.start('Register')

  // Timeout before the redirect to let Mixpanel calls complete.
  // It is less problematic in the main app, where Mixpanel would track
  // missing events on the next page, but in the extension, the next page
  // is the extension auth page, which closes immediately and Mixpanel
  // calls are lost, so we set a longer timeout in this case.
  // See #9509.
  let timeout = 100
  // Redirect to extension auth page if we need to
  if (router.currentRoute.value.query?.redirect === '/extension-auth') {
    nextPage = { name: 'extension_auth' }
    timeout = 3000
  }

  if (nextPage) {
    setTimeout(() => {
      router.push(nextPage)
      wait.end('Register')
    }, timeout)
  } else {
    setTimeout(() => {
      props.nextOnboardingPage()
      wait.end('Register')
    }, timeout)
  }
}

function onError(): void {
  wait.end('Register')
}

onBeforeMount(async () => {
  socialView.value = await createSocialAuthView(onStart, onSuccess, onError)
  if (socialView.value) {
    socialView.value.init()
  }

  // We only show the apple button on apple devices (browser or native app)

  if (isAppleDevice() || (await platform.isNativeIOSApp())) {
    primaryButton.value = 'apple'
  }

  isGoogleNative.value = await platform.isNativeAndroidApp()

  updateShowGoogleSignupOnMobile()
  window.addEventListener('resize', updateShowGoogleSignupOnMobile)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateShowGoogleSignupOnMobile)
})

const _view = computed(() => {
  return assertNotNull(view.value)
})

onMounted(async () => {
  // If user name was passed a prop (from the signup with email form)
  if (route.query.email) {
    _view.value.model.email = route.query.email as string
  }

  authCssFix()
})

async function submitForm(): Promise<void> {
  await _view.value.start()
}

const loginRoute = computed(() => {
  const routeData = { name: 'login', query: route.query }
  // If we have the email set, pass it on to the signup page.
  if (_view.value.model.email) {
    routeData.query.email = _view.value.model.email
  }
  return routeData
})

const isTemporaryUser = computed(() => {
  return auth.isTemporaryUser()
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_v_wait = _resolveComponent("v-wait")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.$wait.is('Register'))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[4] || (_cache[4] = _createElementVNode("p", { class: "onboarding-signup__loading-text" }, "Creating account", -1)),
            _createVNode(ButtonSpinner)
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      (isTemporaryUser.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "onboarding-signup__back-15807",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).back()))
          }, [...(_cache[5] || (_cache[5] = [
            _createElementVNode("span", { class: "iconfont iback" }, null, -1),
            _createTextVNode(" Back ", -1)
          ]))]))
        : _createCommentVNode("", true),
      _cache[6] || (_cache[6] = _createElementVNode("div", null, [
        _createElementVNode("img", {
          class: "onboarding-signup__logo",
          src: _imports_0,
          alt: "Shortform App"
        })
      ], -1)),
      _createVNode(Slider)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(BottomSheet, {
        ref_key: "bottomSheetRef",
        ref: bottomSheetRef,
        innerClass: "onboarding-signup__sheet"
      }, {
        default: _withCtx(() => [
          _createVNode(SignupSheet, { view: view.value }, null, 8, ["view"])
        ]),
        _: 1
      }, 512),
      _createElementVNode("p", _hoisted_6, [
        _cache[9] || (_cache[9] = _createTextVNode(" By creating an account, you agree to the ", -1)),
        _createVNode(_component_router_link, { to: { name: 'site_terms' } }, {
          default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
            _createTextVNode("Terms of Service", -1)
          ]))]),
          _: 1
        }),
        _cache[10] || (_cache[10] = _createTextVNode(" and ", -1)),
        _createVNode(_component_router_link, { to: { name: 'site_privacy' } }, {
          default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
            _createTextVNode("Privacy Policy", -1)
          ]))]),
          _: 1
        }),
        _cache[11] || (_cache[11] = _createTextVNode(". ", -1))
      ]),
      _createElementVNode("div", _hoisted_7, [
        (socialView.value?.appleAuthView && primaryButton.value === 'apple')
          ? (_openBlock(), _createBlock(AppleSignInButton, {
              key: 0,
              class: "onboarding-signup__primary-button onboarding-signup__button--apple",
              view: socialView.value.appleAuthView,
              title: "Sign up with"
            }, null, 8, ["view"]))
          : (socialView.value?.googleAuthView && showGoogleSignupOnMobile.value)
            ? (_openBlock(), _createBlock(GoogleSignInButton, {
                key: 1,
                authMode: authMode.value,
                view: socialView.value.googleAuthView,
                class: _normalizeClass(["onboarding-signup__primary-button onboarding-signup__primary-button--google", {
            'onboarding-signup__primary-button--google-native': isGoogleNative.value,
          }]),
                showButton: "big"
              }, null, 8, ["authMode", "view", "class"]))
            : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", {
        class: _normalizeClass(["onboarding-signup__buttons", {
          'onboarding-signup__buttons--small':
            primaryButton.value === 'google' && !isGoogleNative.value,
        }])
      }, [
        (
            socialView.value?.appleAuthView &&
            !isGoogleNative.value &&
            primaryButton.value !== 'apple'
          )
          ? (_openBlock(), _createBlock(AppleSignInButton, {
              key: 0,
              class: "onboarding-signup__button onboarding-signup__button--apple",
              view: socialView.value.appleAuthView,
              title: "Sign up with"
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        (
            socialView.value?.googleAuthView &&
            showGoogleSignupOnMobile.value &&
            !isGoogleNative.value &&
            primaryButton.value !== 'google'
          )
          ? (_openBlock(), _createBlock(GoogleSignInButton, {
              key: 1,
              authMode: authMode.value,
              view: socialView.value.googleAuthView,
              class: "onboarding-signup__button onboarding-signup__button--google",
              showButton: "small"
            }, null, 8, ["authMode", "view"]))
          : _createCommentVNode("", true),
        (socialView.value?.facebookAuthView)
          ? (_openBlock(), _createBlock(FacebookLoginButton, {
              key: 2,
              title: "",
              view: socialView.value.facebookAuthView,
              class: "onboarding-signup__button onboarding-signup__button--facebook"
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          class: "onboarding-signup__button onboarding-signup__button--email",
          onClick: openInfoSheet
        }, [...(_cache[12] || (_cache[12] = [
          _createElementVNode("img", {
            class: "onboarding-signup__button-image",
            src: _imports_1,
            alt: ""
          }, null, -1)
        ]))])
      ], 2),
      _cache[15] || (_cache[15] = _createElementVNode("div", { class: "onboarding-signup__separator" }, [
        _createElementVNode("img", {
          src: _imports_2,
          alt: ""
        })
      ], -1)),
      _createElementVNode("div", _hoisted_8, [
        _cache[14] || (_cache[14] = _createTextVNode(" Already have an account? ", -1)),
        _createVNode(_component_router_link, {
          to: { name: 'login', query: _unref(route).query }
        }, {
          default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
            _createTextVNode("Log in", -1)
          ]))]),
          _: 1
        }, 8, ["to"])
      ])
    ]),
    (view.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _cache[29] || (_cache[29] = _createElementVNode("h2", { class: "shortform-sign-up__title" }, "Sign Up to Shortform", -1)),
              _createElementVNode("form", {
                class: "shortform-sign-up__form",
                role: "form",
                novalidate: "",
                onSubmit: _withModifiers(submitForm, ["prevent"])
              }, [
                _createElementVNode("div", _hoisted_12, [
                  _cache[16] || (_cache[16] = _createElementVNode("label", {
                    class: "shortform-sign-up__label",
                    for: "register_full_name"
                  }, "Full name", -1)),
                  _createVNode(InputWithContextMenu, {
                    id: "register_full_name",
                    modelValue: view.value.model.full_name,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((view.value.model.full_name) = $event)),
                    class: _normalizeClass(["shortform-sign-up__input", view.value.validation.stateClass('full_name')]),
                    type: "text",
                    placeholder: "",
                    autofocus: ""
                  }, null, 8, ["modelValue", "class"]),
                  _createVNode(FormError, {
                    errors: view.value.validation.errors,
                    field: "full_name"
                  }, null, 8, ["errors"])
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _cache[17] || (_cache[17] = _createElementVNode("label", {
                    class: "shortform-sign-up__label",
                    for: "register_email"
                  }, "Email Address", -1)),
                  _createVNode(InputWithContextMenu, {
                    id: "register_email",
                    modelValue: view.value.model.email,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((view.value.model.email) = $event)),
                    class: _normalizeClass(["shortform-sign-up__input", view.value.validation.stateClass('email')]),
                    type: "email",
                    placeholder: "",
                    autofocus: ""
                  }, null, 8, ["modelValue", "class"]),
                  _createVNode(FormError, {
                    errors: view.value.validation.errors,
                    field: "email"
                  }, null, 8, ["errors"])
                ]),
                _createElementVNode("div", _hoisted_14, [
                  _cache[18] || (_cache[18] = _createElementVNode("label", {
                    class: "shortform-sign-up__label",
                    for: "register_password"
                  }, "Password", -1)),
                  _createVNode(InputWithContextMenu, {
                    id: "register_password",
                    modelValue: view.value.model.password,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((view.value.model.password) = $event)),
                    type: "password",
                    autocomplete: "new-password",
                    placeholder: "",
                    class: _normalizeClass(["shortform-sign-up__input", view.value.validation.stateClass('password')])
                  }, null, 8, ["modelValue", "class"]),
                  _createVNode(FormError, {
                    errors: view.value.validation.errors,
                    field: "password"
                  }, null, 8, ["errors"])
                ]),
                (view.value.model.password_score)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                      _createElementVNode("small", null, [
                        _cache[19] || (_cache[19] = _createTextVNode(" password strength: ", -1)),
                        (view.value.model.password_score === '4')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_16, "strong"))
                          : _createCommentVNode("", true),
                        (view.value.model.password_score === '3')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_17, "good"))
                          : _createCommentVNode("", true),
                        (view.value.model.password_score === '2')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_18, "medium"))
                          : _createCommentVNode("", true),
                        (view.value.model.password_score === '1')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_19, "weak"))
                          : _createCommentVNode("", true),
                        (view.value.model.password_score === '0')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_20, "terrible"))
                          : _createCommentVNode("", true)
                      ])
                    ]))
                  : _createCommentVNode("", true),
                (view.value.validation.errors && view.value.validation.errors._schema)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_21, " {view.validation.errors._schema} "))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_22, [
                  _createElementVNode("span", _hoisted_23, [
                    _cache[22] || (_cache[22] = _createTextVNode("By creating an account, you agree to the ", -1)),
                    _createVNode(_component_router_link, {
                      to: { name: 'site_terms' },
                      class: "black",
                      target: "_blank"
                    }, {
                      default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                        _createTextVNode("Terms of Service", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[23] || (_cache[23] = _createTextVNode(" and ", -1)),
                    _createVNode(_component_router_link, {
                      to: { name: 'site_privacy' },
                      class: "black",
                      target: "_blank"
                    }, {
                      default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                        _createTextVNode("Privacy Policy", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[24] || (_cache[24] = _createTextVNode(". ", -1))
                  ])
                ]),
                _createElementVNode("div", _hoisted_24, [
                  _createVNode(_component_v_wait, {
                    for: "Register",
                    style: {"display":"flex"}
                  }, {
                    waiting: _withCtx(() => [
                      _createElementVNode("div", _hoisted_25, [
                        _cache[25] || (_cache[25] = _createTextVNode(" Creating Account ", -1)),
                        _createVNode(ButtonSpinner)
                      ])
                    ]),
                    default: _withCtx(() => [
                      _cache[26] || (_cache[26] = _createElementVNode("button", {
                        class: "shortform-sign-up__submit button button--theme",
                        type: "submit"
                      }, " Create Account ", -1))
                    ]),
                    _: 1
                  })
                ])
              ], 32),
              _createVNode(SocialAuth, {
                onStart: onStart,
                onSuccess: onSuccess,
                onError: onError,
                authMode: authMode.value,
                showGoogle: !showGoogleSignupOnMobile.value
              }, null, 8, ["authMode", "showGoogle"]),
              _createElementVNode("div", null, [
                _createElementVNode("p", _hoisted_26, [
                  _cache[28] || (_cache[28] = _createTextVNode(" Already have an account? ", -1)),
                  _createVNode(_component_router_link, {
                    to: loginRoute.value,
                    style: {"color":"#ffb600"}
                  }, {
                    default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                      _createElementVNode("strong", null, "Log in", -1)
                    ]))]),
                    _: 1
                  }, 8, ["to"])
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})